import { ibhApiService } from '@hub/bearing-hub/common/api-service';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { IbhCustomArticlePriceScalesParams, IbhShape } from '@igus/iglidur-bearing-hub-api';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type {
  IbhArticlePriceScalesResponseDto,
  IbhCatalogArticlePriceScalesParams,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { useCallback, useState } from 'react';
import type { UnitSystem } from '@igus/kopla-domain';

type UseBearingArticlePriceScalesApiProps = {
  articleNumber: string;
  isCustom: boolean;
  shape: IbhShape;
  quantity: number;
  unitSystem: UnitSystem;
  dimensions?: IbhDimensions;
  session?: Session;
};

export const useBearingArticlePriceScalesApi = ({
  articleNumber,
  isCustom,
  shape,
  quantity,
  unitSystem,
  dimensions,
  session,
}: UseBearingArticlePriceScalesApiProps) => {
  const { sessionId } = session ?? {};
  const [priceScales, setPriceScales] = useState<IbhArticlePriceScalesResponseDto[] | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPriceScales = useCallback(async () => {
    if (priceScales || !articleNumber || !sessionId) {
      return;
    }

    try {
      const catalogArticlePayload = {
        articleNumber,
        sid: sessionId,
      } as IbhCatalogArticlePriceScalesParams;

      const customArticlePayload = {
        ...catalogArticlePayload,
        quantity,
        shape,
        unitSystem,
        ...dimensions,
      } as IbhCustomArticlePriceScalesParams;

      setLoading(true);
      const response = isCustom
        ? await ibhApiService.customArticlePriceScales(customArticlePayload)
        : await ibhApiService.catalogArticlePriceScales(catalogArticlePayload);

      setPriceScales(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(`Failed to fetch article ${articleNumber} price scales`);
    }
  }, [articleNumber, session]);

  return { priceScales, loading, error, fetchPriceScales };
};
